/*
 * LT-14 Phase 4b — Milestones settings tab.
 *
 * Two sections:
 *   1. Library — flat master list of milestone titles. Add / rename /
 *      reorder / archive. Same behaviour as the Master Phase List tab;
 *      a rename here propagates to every template referencing this
 *      milestone_id because templates key by id, never by name.
 *
 *   2. Templates per job type — pick a job type, edit its ordered
 *      milestones + allocations.
 *
 * The allocation UX in the templates section — the part to get right:
 *   - Default is one dropdown per cost item: pick its milestone.
 *     That is the 90% case and stays one click.
 *   - "Split" expands a cost item into percentage rows across two or
 *     more milestones. Running total per item is shown live.
 *   - Save is blocked while any cost item's allocations don't total
 *     100%, with the offending item named. No round-to-fix silently.
 *   - "Un-split" snaps the largest weight to 1.0 and drops the sibling
 *     rows — atomically. A zero-weight row left behind looks like a
 *     decision someone made; snap-and-drop keeps stored allocations
 *     honest.
 *
 * Save is a WHOLESALE replace: server sends the whole ordered list,
 * server validates the lot, 400 names the offender. The invariant is
 * cross-milestone (a phase's weights span several milestones), so a
 * partial PATCH can't be validated without reading everything anyway.
 *
 * Optimistic concurrency via `expected_updated_at`: whatever the
 * server returned on the last GET goes back on the next PUT. Server
 * responds 409 if the template has moved on since — the client then
 * reloads rather than silently clobber the other editor.
 *
 * Snapshot-onto-tender (Phase 4c) is NOT in this tab. This is Settings
 * only.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, Save, Plus, Archive, ChevronUp, ChevronDown, X, GitBranch,
  Copy, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  Card, Field, Input, NumberInput, PrimaryButton, QuietButton,
} from "../../components/UI";
import { useJobTypeLibrary } from "../../hooks/useJobTypeLibrary";

const cls = (...xs) => xs.filter(Boolean).join(" ");

// LT-15 — the hardcoded JOB_TYPES const that used to live here is gone;
// the job-type dropdown reads from `useJobTypeLibrary()` so a rename or
// add in Settings → Job Types flows through immediately.

// Weight comparison — matches ALLOCATION_TOLERANCE in payment_schedule.py.
// Keep in step; the server enforces the same 0.001 slack.
const TOL = 0.001;

export function MilestonesTab() {
  return (
    <div className="flex flex-col gap-5" data-testid="milestones-tab">
      <LibrarySection />
      <TemplateSection />
    </div>
  );
}

/* ================================================================== *
 *  Library section — the same PhasesTab shape, adapted to the LT-14
 *  field names (display_order / archived rather than default_sequence
 *  / is_archived).
 * ================================================================== */

function LibrarySection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");

  const load = () =>
    api.get("/settings/milestone-library")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load milestones"))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async () => {
    const name = adding.trim();
    if (!name) return;
    try {
      await api.post("/settings/milestone-library", { name });
      setAdding("");
      toast.success("Milestone added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add milestone");
    }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    try {
      await api.put("/settings/milestone-library/reorder", {
        order: next.map((m) => m.id),
      });
    } catch {
      toast.error("Reorder failed");
      load();
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this milestone? Templates still using it will keep the reference; it's just hidden from pickers.")) return;
    try {
      await api.delete(`/settings/milestone-library/${id}`);
      toast.success("Milestone archived");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to archive");
    }
  };

  const startRename = (item) => {
    setRenamingId(item.id);
    setRenameDraft(item.name);
  };
  const cancelRename = () => { setRenamingId(null); setRenameDraft(""); };
  const commitRename = async () => {
    const name = renameDraft.trim();
    if (!name || !renamingId) return cancelRename();
    try {
      await api.patch(`/settings/milestone-library/${renamingId}`, { name });
      toast.success("Milestone renamed");
      cancelRename();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  if (loading) return <div className="text-ink-3" style={{ fontSize: 12 }}>Loading…</div>;

  return (
    <div data-testid="milestone-library-card">
    <Card className="p-5">
      <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
        Milestone library
      </h3>
      <p className="text-ink-3 mt-1 mb-4" style={{ fontSize: 12 }}>
        The master list of stage names available to every job-type template. Rename here and every template referencing this milestone updates automatically — templates key by id, not by title.
      </p>

      <div className="flex flex-col gap-1.5 mb-4">
        {items.map((m, i) => (
          <div
            key={m.id}
            className="rounded-md border border-line bg-surface"
            data-testid={`milestone-library-row-${m.id}`}
          >
            <div className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span
                  className="font-mono tnum text-ink-3 flex-shrink-0"
                  style={{ fontSize: 11 }}
                >
                  {String(m.display_order).padStart(2, "0")}
                </span>
                {renamingId === m.id ? (
                  <input
                    type="text"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename();
                      if (e.key === "Escape") cancelRename();
                    }}
                    className="flex-1 min-w-0 rounded border border-accent/40 bg-surface-2 px-2 py-1 text-ink outline-none"
                    style={{ fontSize: 13 }}
                    autoFocus
                    data-testid={`milestone-library-rename-input-${m.id}`}
                  />
                ) : (
                  <button
                    className="text-ink text-left flex-1 min-w-0 hover:text-accent"
                    style={{ fontSize: 13 }}
                    onClick={() => startRename(m)}
                    title="Click to rename"
                    data-testid={`milestone-library-name-${m.id}`}
                  >
                    {m.name}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => move(i, -1)}
                  className="h-7 w-7 rounded text-ink-3 hover:bg-surface-2 hover:text-ink"
                  disabled={i === 0}
                  data-testid={`milestone-library-up-${m.id}`}
                ><ChevronUp size={13} className="mx-auto" /></button>
                <button
                  onClick={() => move(i, 1)}
                  className="h-7 w-7 rounded text-ink-3 hover:bg-surface-2 hover:text-ink"
                  disabled={i === items.length - 1}
                  data-testid={`milestone-library-down-${m.id}`}
                ><ChevronDown size={13} className="mx-auto" /></button>
                <button
                  onClick={() => archive(m.id)}
                  className="h-7 px-2 rounded text-ink-3 hover:text-note-red hover:bg-surface-2"
                  title="Archive"
                  data-testid={`milestone-library-archive-${m.id}`}
                ><Archive size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div
            className="text-ink-3 italic px-3 py-4 text-center border border-dashed border-line-soft rounded-md"
            style={{ fontSize: 12 }}
          >
            No milestones yet. Add one below.
          </div>
        )}
      </div>

      <div className="flex items-end gap-2">
        <Field label="Add a milestone">
          <Input
            value={adding}
            onChange={setAdding}
            placeholder="e.g. Roof watertight"
            onKeyDown={(e) => e.key === "Enter" && add()}
            data-testid="milestone-library-add-input"
          />
        </Field>
        <QuietButton
          onClick={add}
          icon={Plus}
          disabled={!adding.trim()}
          data-testid="milestone-library-add-btn"
        >
          Add
        </QuietButton>
      </div>
    </Card>
    </div>
  );
}


/* ================================================================== *
 *  Template section — job type picker + milestone list + per-cost-item
 *  allocation editor. Save is a wholesale replace with an
 *  expected_updated_at precondition (409 on race).
 * ================================================================== */

// State kept in the raw storage shape (`milestones: [{milestone_id,
// claims: {phase_id: w}, claims_match: {kw: w}}]`) so save is trivial.
// The allocation UI derives its rows from this. All mutations MUST go
// through the two helpers below so an item that ends up at 0 weight is
// removed from its claims dict (rather than left as an explicit zero,
// which reads as "deliberately zero" later).
function setWeightImmutable(tpl, kind, itemKey, milestoneId, weight) {
  const field = kind === "phase" ? "claims" : "claims_match";
  return {
    ...tpl,
    milestones: tpl.milestones.map((m) => {
      if (m.milestone_id !== milestoneId) return m;
      const nextClaims = { ...(m[field] || {}) };
      if (weight > TOL) nextClaims[itemKey] = Number(weight);
      else delete nextClaims[itemKey];
      return { ...m, [field]: nextClaims };
    }),
  };
}

function totalFor(tpl, kind, itemKey) {
  const field = kind === "phase" ? "claims" : "claims_match";
  return tpl.milestones.reduce(
    (sum, m) => sum + ((m[field] || {})[itemKey] || 0), 0
  );
}

function nonZeroMilestones(tpl, kind, itemKey) {
  const field = kind === "phase" ? "claims" : "claims_match";
  const out = [];
  for (const m of tpl.milestones) {
    const w = (m[field] || {})[itemKey] || 0;
    if (w > TOL) out.push({ milestone_id: m.milestone_id, weight: w });
  }
  return out;
}

function TemplateSection() {
  const { activeTypes: jobTypes, loading: jtLoading } = useJobTypeLibrary();
  const [jobType, setJobType] = useState("");
  const [tpl, setTpl] = useState(null);
  const [library, setLibrary] = useState([]);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Cost items the user has explicitly opened the split editor for. An
  // item can also be effectively-split by data (>1 milestone with weight
  // > 0), which we treat identically at render time.
  const [splitPhases, setSplitPhases] = useState(new Set());
  const [splitKeywords, setSplitKeywords] = useState(new Set());
  // LT-15 Build 3 — "Copy from another job type" state. Only meaningful
  // when the current template is empty; the button is hidden otherwise.
  const [copyFromKey, setCopyFromKey] = useState("");
  const [copying, setCopying] = useState(false);

  // Seed the picker from the library once loaded. Prefer whichever type
  // was previously in state (so re-mounts don't jump); otherwise the
  // first active type.
  useEffect(() => {
    if (!jobType && jobTypes.length > 0) {
      setJobType(jobTypes[0].key);
    }
  }, [jobTypes, jobType]);

  const load = async () => {
    if (!jobType) return;
    setLoading(true);
    try {
      const [t, lib, ph] = await Promise.all([
        api.get(`/settings/milestone-templates/${jobType}`),
        api.get("/settings/milestone-library"),
        api.get("/settings/master-phases"),
      ]);
      setTpl(t.data);
      setLibrary(lib.data || []);
      setPhases(ph.data || []);
      setSplitPhases(new Set());
      setSplitKeywords(new Set());
      setDirty(false);
      setCopyFromKey("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load template");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [jobType]);

  // Titles-by-id lookup for whichever library entries this template uses.
  // Kept as a Map for O(1) lookup inside the render tree.
  const libraryById = useMemo(
    () => new Map(library.map((m) => [m.id, m])),
    [library],
  );

  // Milestones in the CURRENT template, in the order the client last saw.
  const currentMilestones = useMemo(
    () => (tpl?.milestones || []).map((m) => ({
      ...m,
      title: libraryById.get(m.milestone_id)?.name || "(archived milestone)",
    })),
    [tpl, libraryById],
  );

  // Distinct provsum keywords currently in play across the template.
  // Ordered by first-appearance so a keyword added mid-list stays put.
  const keywordsInPlay = useMemo(() => {
    const seen = [];
    for (const m of tpl?.milestones || []) {
      for (const k of Object.keys(m.claims_match || {})) {
        if (!seen.includes(k)) seen.push(k);
      }
    }
    return seen;
  }, [tpl]);

  // Client-side validation mirroring _validate_template on the server.
  // Save is DISABLED (not just server-rejected) while this returns errors,
  // and the offender's name is surfaced in the tooltip.
  const invalidItems = useMemo(() => {
    if (!tpl) return [];
    const bad = [];
    const phaseIds = new Set();
    const kwSet = new Set();
    for (const m of tpl.milestones) {
      for (const k of Object.keys(m.claims || {})) phaseIds.add(k);
      for (const k of Object.keys(m.claims_match || {})) kwSet.add(k);
    }
    for (const pid of phaseIds) {
      const t = totalFor(tpl, "phase", pid);
      if (t > TOL && Math.abs(t - 1.0) > TOL) {
        const name = phases.find((p) => p.id === pid)?.name || pid;
        bad.push({ kind: "phase", id: pid, label: name, total: t });
      }
    }
    for (const kw of kwSet) {
      const t = totalFor(tpl, "kw", kw);
      if (t > TOL && Math.abs(t - 1.0) > TOL) {
        bad.push({ kind: "kw", id: kw, label: `"${kw}"`, total: t });
      }
    }
    return bad;
  }, [tpl, phases]);

  const mutate = (fn) => {
    setTpl((prev) => fn(prev));
    setDirty(true);
  };

  const save = async () => {
    if (invalidItems.length > 0) return;
    setSaving(true);
    try {
      const r = await api.put(
        `/settings/milestone-templates/${jobType}`,
        {
          milestones: tpl.milestones.map((m, idx) => ({
            milestone_id: m.milestone_id,
            display_order: idx,
            claims: m.claims || {},
            claims_match: m.claims_match || {},
          })),
          expected_updated_at: tpl.updated_at || null,
        },
      );
      setTpl(r.data);
      setDirty(false);
      toast.success("Template saved");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error("Template was updated elsewhere — reloading.");
        load();
      } else {
        toast.error(e?.response?.data?.detail || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  /* ---------- milestone-list operations (this template's stages) ---------- */

  const addMilestoneToTemplate = (milestone_id) => {
    if (!milestone_id) return;
    if (tpl.milestones.some((m) => m.milestone_id === milestone_id)) {
      toast.error("Milestone already in this template");
      return;
    }
    mutate((prev) => ({
      ...prev,
      milestones: [
        ...prev.milestones,
        {
          milestone_id,
          display_order: prev.milestones.length,
          claims: {},
          claims_match: {},
        },
      ],
    }));
  };

  const removeMilestoneFromTemplate = (milestone_id) => {
    if (!window.confirm("Remove this milestone from the template? Any cost items claimed only by this milestone will become under-allocated and save will block until you re-assign them.")) return;
    mutate((prev) => ({
      ...prev,
      milestones: prev.milestones.filter((m) => m.milestone_id !== milestone_id),
    }));
  };

  const moveMilestone = (idx, dir) => {
    mutate((prev) => {
      const next = [...prev.milestones];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return { ...prev, milestones: next };
    });
  };

  /* ---------- cost-item allocation editing ---------- */

  const setSingleAllocation = (kind, itemKey, milestoneId) => {
    // "One dropdown" mode: replace whatever's there with either
    // {milestoneId: 1.0} or {} (unallocated). This is the atomic swap
    // that keeps the total exactly 100% (or exactly 0%).
    mutate((prev) => {
      const field = kind === "phase" ? "claims" : "claims_match";
      const next = {
        ...prev,
        milestones: prev.milestones.map((m) => {
          const nextClaims = { ...(m[field] || {}) };
          delete nextClaims[itemKey];
          if (milestoneId && m.milestone_id === milestoneId) {
            nextClaims[itemKey] = 1.0;
          }
          return { ...m, [field]: nextClaims };
        }),
      };
      return next;
    });
  };

  const setSplitWeight = (kind, itemKey, milestoneId, pct) => {
    const weight = Math.max(0, Number(pct || 0) / 100);
    mutate((prev) => setWeightImmutable(prev, kind, itemKey, milestoneId, weight));
  };

  const enterSplit = (kind, itemKey) => {
    if (kind === "phase") setSplitPhases((s) => new Set(s).add(itemKey));
    else                  setSplitKeywords((s) => new Set(s).add(itemKey));
  };

  const unsplit = (kind, itemKey) => {
    // Snap-and-drop. Compute the survivor first, validate, THEN write in
    // a single mutation — no intermediate state where the item is
    // under-allocated (which an autosave / race could persist).
    const survivors = nonZeroMilestones(tpl, kind, itemKey);
    if (survivors.length === 0) {
      // Nothing to un-split — item is already unallocated. Just close
      // the UI mode.
    } else {
      const biggest = survivors.reduce(
        (a, b) => (a.weight >= b.weight ? a : b),
      );
      mutate((prev) => {
        const field = kind === "phase" ? "claims" : "claims_match";
        return {
          ...prev,
          milestones: prev.milestones.map((m) => {
            const nextClaims = { ...(m[field] || {}) };
            delete nextClaims[itemKey];
            if (m.milestone_id === biggest.milestone_id) {
              nextClaims[itemKey] = 1.0;
            }
            return { ...m, [field]: nextClaims };
          }),
        };
      });
    }
    if (kind === "phase") {
      setSplitPhases((s) => { const nx = new Set(s); nx.delete(itemKey); return nx; });
    } else {
      setSplitKeywords((s) => { const nx = new Set(s); nx.delete(itemKey); return nx; });
    }
  };

  const addKeywordAndMilestone = (kw, milestoneId) => {
    const clean = (kw || "").trim().toLowerCase();
    if (!clean) return;
    if (!milestoneId) return;
    if (keywordsInPlay.includes(clean)) {
      toast.error(`Keyword "${clean}" already in this template`);
      return;
    }
    // Neither "seed at 100% on the first milestone" nor a pending-at-0%
    // state — the user picks the target milestone in the same action, so
    // the keyword lands where they intended, at 100%, with no ambiguity
    // and no zero-weight rows to leave around.
    setSingleAllocation("kw", clean, milestoneId);
  };

  /* ---------- LT-15 Build 3 — Copy from another job type ---------- */
  const copyFrom = async () => {
    if (!copyFromKey || !jobType || copyFromKey === jobType) return;
    setCopying(true);
    try {
      const r = await api.post(
        `/settings/milestone-templates/${jobType}/copy-from/${copyFromKey}`,
      );
      setTpl(r.data);
      setDirty(false);
      const count = (r.data?.milestones || []).length;
      toast.success(`Copied ${count} milestone${count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copy failed");
    } finally {
      setCopying(false);
    }
  };

  /* ---------- render ---------- */

  if (loading || !tpl) {
    return <Card className="p-5 text-ink-3" style={{ fontSize: 12 }}>Loading template…</Card>;
  }

  const availableToAdd = library.filter(
    (m) => !tpl.milestones.some((x) => x.milestone_id === m.id),
  );

  const saveDisabled = saving || invalidItems.length > 0 || !dirty;
  const saveDisabledReason =
    saving ? "Saving…" :
    !dirty ? "No unsaved changes" :
    invalidItems.length > 0
      ? `${invalidItems[0].label} is at ${(invalidItems[0].total * 100).toFixed(1)}% — must total 100%`
      : "";

  return (
    <div data-testid="milestone-template-card">
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
            Templates per job type
          </h3>
          <p className="text-ink-3 mt-1" style={{ fontSize: 12 }}>
            Pick a job type, then choose which library milestones apply and how each cost item's value splits across them. Every cost item's allocations must total 100% before saving. Templates key by id, so a rename in the library propagates here automatically.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <label className="col-head" style={{ fontSize: 10 }}>Job type</label>
        <select
          value={jobType}
          onChange={(e) => setJobType(e.target.value)}
          disabled={jtLoading || jobTypes.length === 0}
          className="rounded border border-line bg-surface px-2 py-1 text-ink"
          style={{ fontSize: 13 }}
          data-testid="milestone-template-job-type"
        >
          {jtLoading && <option value="">Loading…</option>}
          {!jtLoading && jobTypes.length === 0 && (
            <option value="">No active job types</option>
          )}
          {jobTypes.map((j) => (
            <option key={j.key} value={j.key}>{j.label}</option>
          ))}
        </select>
        {dirty && (
          <span className="text-note-amber ml-2" style={{ fontSize: 11 }}>
            Unsaved changes
          </span>
        )}
      </div>

      {/* LT-15 Build 3 — Empty template banner + Copy From.
          A payment schedule can't seed on a tender until this template
          has at least one milestone — say so plainly, then give Chris
          a one-click shortcut to copy an existing job type's setup and
          edit from there. Only surfaces when current template is empty. */}
      {currentMilestones.length === 0 && (
        <div
          className="rounded-md border border-note-amber/40 bg-note-amber/10 p-4 mb-5"
          data-testid="milestone-template-empty-state"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-note-amber" />
            <div className="flex-1">
              <div className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
                No milestones on this template yet
              </div>
              <div className="text-ink-2 mt-1" style={{ fontSize: 12 }}>
                Tenders of this job type won't produce a payment schedule until this
                template has at least one milestone. Copy an existing job type's setup
                to start from a known-good template, or build it from scratch below.
              </div>
              <div className="flex items-end gap-2 mt-3 flex-wrap">
                <Field label="Copy from another job type" hint="Milestones and allocations copy verbatim (allocations key by master-phase id).">
                  <select
                    value={copyFromKey}
                    onChange={(e) => setCopyFromKey(e.target.value)}
                    className="h-9 rounded border border-line bg-surface px-2 text-ink"
                    style={{ fontSize: 13, minWidth: 240 }}
                    data-testid="milestone-template-copy-from-select"
                  >
                    <option value="">Choose a source…</option>
                    {jobTypes.filter((j) => j.key !== jobType).map((j) => (
                      <option key={j.key} value={j.key}>{j.label}</option>
                    ))}
                  </select>
                </Field>
                <PrimaryButton
                  onClick={copyFrom}
                  disabled={!copyFromKey || copying}
                  icon={copying ? Loader2 : Copy}
                  data-testid="milestone-template-copy-from-btn"
                >
                  {copying ? "Copying…" : "Copy"}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Milestones in this template ------------------------------- */}
      <h4 className="font-display text-ink mb-1" style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.03em", textTransform: "uppercase" }}>
        Milestones in this template
      </h4>
      <p className="text-ink-3 mb-2" style={{ fontSize: 11 }}>
        Reorder to change the payment order. Remove a milestone to un-allocate everything claimed only by it.
      </p>
      <div className="flex flex-col gap-1.5 mb-3">
        {currentMilestones.map((m, i) => (
          <div
            key={m.milestone_id}
            className="rounded-md border border-line bg-surface flex items-center justify-between gap-3 px-3 py-2"
            data-testid={`template-milestone-row-${m.milestone_id}`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span
                className="font-mono tnum text-ink-3 flex-shrink-0"
                style={{ fontSize: 11 }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-ink" style={{ fontSize: 13 }}>{m.title}</span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => moveMilestone(i, -1)}
                className="h-7 w-7 rounded text-ink-3 hover:bg-surface-2 hover:text-ink"
                disabled={i === 0}
                data-testid={`template-milestone-up-${m.milestone_id}`}
              ><ChevronUp size={13} className="mx-auto" /></button>
              <button
                onClick={() => moveMilestone(i, 1)}
                className="h-7 w-7 rounded text-ink-3 hover:bg-surface-2 hover:text-ink"
                disabled={i === currentMilestones.length - 1}
                data-testid={`template-milestone-down-${m.milestone_id}`}
              ><ChevronDown size={13} className="mx-auto" /></button>
              <button
                onClick={() => removeMilestoneFromTemplate(m.milestone_id)}
                className="h-7 px-2 rounded text-ink-3 hover:text-note-red hover:bg-surface-2"
                title="Remove from template"
                data-testid={`template-milestone-remove-${m.milestone_id}`}
              ><X size={12} /></button>
            </div>
          </div>
        ))}
        {currentMilestones.length === 0 && (
          <div
            className="text-ink-3 italic px-3 py-4 text-center border border-dashed border-line-soft rounded-md"
            style={{ fontSize: 12 }}
          >
            No milestones yet. Add one from the library below.
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mb-6">
        <select
          value=""
          onChange={(e) => addMilestoneToTemplate(e.target.value)}
          disabled={availableToAdd.length === 0}
          className="rounded border border-line bg-surface px-2 py-1 text-ink"
          style={{ fontSize: 12 }}
          data-testid="template-milestone-add-select"
        >
          <option value="">
            {availableToAdd.length === 0
              ? "All library milestones already in this template"
              : "+ Add milestone from library…"}
          </option>
          {availableToAdd.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
      </div>

      {/* Cost item allocations ------------------------------------- */}
      <h4 className="font-display text-ink mb-1" style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.03em", textTransform: "uppercase" }}>
        Cost item allocations
      </h4>
      <p className="text-ink-3 mb-3" style={{ fontSize: 11 }}>
        Each cost item pays into one milestone by default. Use Split if a phase spans several — set the % that each milestone claims. Every row must total 100% (or 0% if the item genuinely doesn't apply to this job type).
      </p>

      <div className="mb-2 col-head" style={{ fontSize: 10 }}>Phases</div>
      <div className="flex flex-col divide-y divide-line-soft border border-line rounded-md mb-6">
        {phases.map((p) => (
          <AllocationRow
            key={p.id}
            kind="phase"
            itemKey={p.id}
            label={p.name}
            tpl={tpl}
            milestones={currentMilestones}
            isSplit={splitPhases.has(p.id) || nonZeroMilestones(tpl, "phase", p.id).length > 1}
            onSetSingle={(mid) => setSingleAllocation("phase", p.id, mid)}
            onSetSplitWeight={(mid, pct) => setSplitWeight("phase", p.id, mid, pct)}
            onEnterSplit={() => enterSplit("phase", p.id)}
            onUnsplit={() => unsplit("phase", p.id)}
          />
        ))}
        {phases.length === 0 && (
          <div
            className="text-ink-3 italic px-3 py-4 text-center"
            style={{ fontSize: 12 }}
          >
            No master phases configured yet.
          </div>
        )}
      </div>

      <div className="mb-2 flex items-center justify-between">
        <div>
          <div className="col-head" style={{ fontSize: 10 }}>Prov-sum keywords</div>
          <p className="text-ink-3" style={{ fontSize: 10.5 }}>
            Substring match against the prov-sum's description. Keywords never apply to phases.
          </p>
        </div>
      </div>
      <div className="flex flex-col divide-y divide-line-soft border border-line rounded-md mb-3">
        {keywordsInPlay.map((kw) => (
          <AllocationRow
            key={kw}
            kind="kw"
            itemKey={kw}
            label={`"${kw}"`}
            tpl={tpl}
            milestones={currentMilestones}
            isSplit={splitKeywords.has(kw) || nonZeroMilestones(tpl, "kw", kw).length > 1}
            onSetSingle={(mid) => setSingleAllocation("kw", kw, mid)}
            onSetSplitWeight={(mid, pct) => setSplitWeight("kw", kw, mid, pct)}
            onEnterSplit={() => enterSplit("kw", kw)}
            onUnsplit={() => unsplit("kw", kw)}
          />
        ))}
        {keywordsInPlay.length === 0 && (
          <div
            className="text-ink-3 italic px-3 py-3 text-center"
            style={{ fontSize: 12 }}
          >
            No prov-sum keywords in this template yet.
          </div>
        )}
      </div>

      <KeywordAdder onAdd={addKeywordAndMilestone} milestones={currentMilestones} />

      {/* Save row -------------------------------------------------- */}
      <div className="mt-6 flex items-center justify-end gap-3">
        {invalidItems.length > 0 && (
          <span
            className="text-note-red"
            style={{ fontSize: 12 }}
            data-testid="milestone-template-invalid-label"
          >
            {invalidItems.length === 1
              ? `${invalidItems[0].label} is at ${(invalidItems[0].total * 100).toFixed(1)}%`
              : `${invalidItems.length} items don't total 100%`}
          </span>
        )}
        <PrimaryButton
          onClick={save}
          disabled={saveDisabled}
          icon={saving ? Loader2 : Save}
          title={saveDisabledReason}
          data-testid="milestone-template-save"
        >
          Save template
        </PrimaryButton>
      </div>
    </Card>
    </div>
  );
}


/* ================================================================== *
 *  AllocationRow — one row per cost item. Two modes:
 *    - Single: one dropdown of milestones (the 90% case, one click).
 *    - Split:  N rows of [milestone select] [%] with a running total.
 *  A cost item is rendered in Split mode when the user opens split OR
 *  when the stored data already spreads it across >1 milestone.
 * ================================================================== */

/* SplitPctInput — wrapper around NumberInput that holds its editing
 * value as a string LOCAL to the input, converting to the parent's
 * weight (0..1 proportion) only on change. See PaymentScheduleTab
 * for the full rationale — inlined here to avoid a cross-file refactor
 * for two callers. */
function SplitPctInput({ weight, onChange, ...rest }) {
  const canonical = (w) => {
    if (!Number.isFinite(w) || Math.abs(w) < 0.00001) return "";
    const one_dp = Math.round(w * 1000) / 10;
    return String(one_dp);
  };
  const [txt, setTxt] = useState(() => canonical(weight));
  useEffect(() => {
    const shown = txt === "" ? 0 : Number(txt) / 100;
    if (Number.isNaN(shown) || Math.abs(shown - (weight || 0)) > 0.0005) {
      setTxt(canonical(weight));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weight]);
  return (
    <NumberInput
      value={txt}
      onChange={(v) => {
        setTxt(v === null || v === undefined ? "" : String(v));
        onChange(v);
      }}
      {...rest}
    />
  );
}


function AllocationRow({
  kind, itemKey, label, tpl, milestones, isSplit,
  onSetSingle, onSetSplitWeight, onEnterSplit, onUnsplit,
}) {
  const total = totalFor(tpl, kind, itemKey);
  const totalPct = total * 100;
  const totalOk = total < TOL || Math.abs(total - 1.0) < TOL;
  const totalLabel = total < TOL ? "0%" : `${totalPct.toFixed(1)}%`;
  const rows = milestones.map((m) => {
    const field = kind === "phase" ? "claims" : "claims_match";
    const src = tpl.milestones.find((x) => x.milestone_id === m.milestone_id);
    return {
      milestone_id: m.milestone_id,
      title: m.title,
      weight: (src?.[field] || {})[itemKey] || 0,
    };
  });
  const primaryId = (() => {
    const nz = rows.filter((r) => r.weight > TOL);
    if (nz.length !== 1) return "";
    return nz[0].milestone_id;
  })();

  return (
    <div
      className="px-3 py-2.5 flex items-start justify-between gap-3"
      data-testid={`allocation-row-${kind}-${itemKey}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cls(
              "text-ink flex-shrink-0",
              kind === "kw" && "italic text-ink-2",
            )}
            style={{ fontSize: 13 }}
          >
            {label}
          </span>
          <span
            className={cls(
              "rounded-full px-2 py-0.5 font-mono tnum flex-shrink-0",
              totalOk
                ? "bg-surface-2 text-ink-3"
                : "bg-note-red/10 text-note-red border border-note-red/30",
            )}
            style={{ fontSize: 10.5 }}
            data-testid={`allocation-total-${kind}-${itemKey}`}
          >
            {totalLabel}
          </span>
        </div>
        {isSplit ? (
          <div className="mt-2 flex flex-col gap-1.5 pl-2 border-l border-line-soft">
            {rows.map((r) => (
              <div
                key={r.milestone_id}
                className="flex items-center gap-2"
                data-testid={`allocation-split-input-${kind}-${itemKey}-${r.milestone_id}`}
              >
                <span className="text-ink-3 flex-1 truncate" style={{ fontSize: 12 }}>
                  {r.title}
                </span>
                <SplitPctInput
                  weight={r.weight}
                  onChange={(v) => onSetSplitWeight(r.milestone_id, v)}
                  step="0.1"
                  min={0}
                  max={100}
                  suffix="%"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-1.5">
            <select
              value={primaryId}
              onChange={(e) => onSetSingle(e.target.value || null)}
              className="w-full rounded border border-line bg-surface px-2 py-1 text-ink"
              style={{ fontSize: 12.5 }}
              data-testid={`allocation-single-select-${kind}-${itemKey}`}
            >
              <option value="">— Not allocated —</option>
              {milestones.map((m) => (
                <option key={m.milestone_id} value={m.milestone_id}>
                  {m.title}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="flex-shrink-0 pt-0.5">
        {isSplit ? (
          <button
            type="button"
            onClick={onUnsplit}
            className="h-7 px-2 rounded text-ink-3 hover:text-ink hover:bg-surface-2 inline-flex items-center gap-1"
            style={{ fontSize: 11 }}
            title="Un-split: snap the largest weight to 100% and drop the others (atomic)"
            data-testid={`allocation-unsplit-${kind}-${itemKey}`}
          >
            Un-split
          </button>
        ) : (
          <button
            type="button"
            onClick={onEnterSplit}
            disabled={milestones.length < 2}
            className="h-7 px-2 rounded text-ink-3 hover:text-ink hover:bg-surface-2 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ fontSize: 11 }}
            title={
              milestones.length < 2
                ? "Add at least two milestones to the template to split"
                : "Split this item across several milestones"
            }
            data-testid={`allocation-split-${kind}-${itemKey}`}
          >
            <GitBranch size={11} /> Split
          </button>
        )}
      </div>
    </div>
  );
}


function KeywordAdder({ onAdd, milestones }) {
  const [kw, setKw] = useState("");
  const [mid, setMid] = useState("");
  const canAdd = kw.trim() && mid && milestones.length > 0;
  const submit = () => {
    if (!canAdd) return;
    onAdd(kw, mid);
    setKw("");
    setMid("");
  };
  return (
    <div className="flex items-end gap-2">
      <Field
        label="Add a prov-sum keyword"
        hint="Case-insensitive substring — matches any prov sum whose description contains this word."
      >
        <Input
          value={kw}
          onChange={setKw}
          placeholder="e.g. glazing"
          onKeyDown={(e) => e.key === "Enter" && submit()}
          data-testid="template-keyword-add-input"
        />
      </Field>
      <Field label="Attach to milestone">
        <select
          value={mid}
          onChange={(e) => setMid(e.target.value)}
          disabled={milestones.length === 0}
          className="rounded border border-line bg-surface px-2 py-1 text-ink"
          style={{ fontSize: 13, minWidth: 220 }}
          data-testid="template-keyword-add-milestone"
        >
          <option value="">
            {milestones.length === 0 ? "Add a milestone first…" : "Choose milestone…"}
          </option>
          {milestones.map((m) => (
            <option key={m.milestone_id} value={m.milestone_id}>{m.title}</option>
          ))}
        </select>
      </Field>
      <QuietButton
        onClick={submit}
        icon={Plus}
        disabled={!canAdd}
        data-testid="template-keyword-add-btn"
      >
        Add
      </QuietButton>
    </div>
  );
}
